import { Box, Link, Stack, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  COMPACT_ROW_ID_SX,
  COMPACT_ROW_META_SX,
  COMPACT_ROW_NAME_SX,
  COMPACT_ROW_STATUS_LABEL_SX,
  COMPACT_ROW_SX,
  CompactRowList,
  RowPipe,
} from 'src/components/CompactRowList';
import Iconify from 'src/components/Iconify';
import { useWin32AppPolicy } from 'src/hooks/wdac';
import { MANAGED_POLICIES_ROUTE } from 'src/pages/environment/constants';
import { getBasePolicyModeLabel } from 'src/utils/enums/basePolicyMode';

const SECTION_HELP =
  "The base policies this app's security catalog is versioned for, one supplemental policy each. Which they are is chosen when you build the app's trusted artifacts.";

// The base policies a trusted app's catalog covers, drawn like its imports above them. Only the
// active ones are part of the trust; a deselected one keeps its policy row but no longer versions.
// With none there is nothing to list, and the card's warning says what is missing instead.
const Win32TrustedBasePolicies = ({ globalApplicationVersionId, onEditArtifacts }) => {
  const navigate = useNavigate();
  const { data: policyState } = useWin32AppPolicy({ globalApplicationVersionId });
  const activePolicies = (policyState?.policies || []).filter(policy => policy.isActive);

  if (activePolicies.length === 0) {
    return null;
  }

  return (
    <Stack gap={0.5}>
      <Stack direction="row" alignItems="center" gap={0.75}>
        <Typography sx={{ color: '#9CA3AF', fontSize: '0.7rem', fontWeight: 600 }}>
          Managed Base Policies
        </Typography>
        <Tooltip
          title={SECTION_HELP}
          arrow
          componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 380 } } }}>
          <Box component="span" display="inline-flex">
            <Iconify icon="material-symbols:info-outline" width={14} height={14} color="#9CA3AF" />
          </Box>
        </Tooltip>
      </Stack>

      <CompactRowList>
        {activePolicies.map(policy => (
          <Stack
            key={policy.id}
            direction="row"
            gap={1.25}
            alignItems="center"
            // TODO: open the base policy itself once the managed policies page can be linked into.
            onClick={() => navigate(MANAGED_POLICIES_ROUTE)}
            sx={COMPACT_ROW_SX}>
            <Box display="inline-flex" sx={{ flexShrink: 0 }}>
              <Iconify icon="mdi:shield-outline" width={20} height={20} color="#9CA3AF" />
            </Box>
            <Typography sx={COMPACT_ROW_NAME_SX}>
              {policy.basePolicyName || policy.policyName}
            </Typography>
            <Typography sx={COMPACT_ROW_ID_SX}>{policy.basePolicyId || '—'}</Typography>
            {policy.basePolicyMode ? (
              <Typography sx={COMPACT_ROW_META_SX}>
                {getBasePolicyModeLabel(policy.basePolicyMode)}
              </Typography>
            ) : null}
            <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
              <RowPipe />
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={event => {
                  event.stopPropagation();
                  onEditArtifacts();
                }}
                sx={{ ...COMPACT_ROW_STATUS_LABEL_SX, color: '#87C8E0' }}>
                Included
              </Link>
            </Stack>
          </Stack>
        ))}
      </CompactRowList>
    </Stack>
  );
};

export default Win32TrustedBasePolicies;
